import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const LandingPage = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeFaq, setActiveFaq] = useState(null);
  const footerRef = useRef(null);

  useEffect(() => {
    if (menuOpen) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }
    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, [menuOpen]);

  // Intersection Observer to hide sticky bar when footer is visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        const stickyBar = document.querySelector('.sticky-bar');
        if (stickyBar) {
          stickyBar.style.display = entry.isIntersecting ? 'none' : 'block';
        }
      },
      { threshold: 0.1 }
    );
    if (footerRef.current) {
      observer.observe(footerRef.current);
    }
    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id) => {
    setMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  return (
    <div className="landing-apple">
      {/* ===== NAVIGATION ===== */}
      <nav className="navbar-apple">
        <div className="nav-container">
          <div className="logo">More MVA Cases</div>
          <button
            className={`hamburger ${menuOpen ? 'active' : ''}`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
          <div className="nav-links">
            <button onClick={() => scrollToSection('models')} className="nav-link-btn">Models</button>
            <button onClick={() => scrollToSection('about')} className="nav-link-btn">About</button>
            <button onClick={() => scrollToSection('faq')} className="nav-link-btn">FAQ</button>
            <button onClick={() => scrollToSection('contact')} className="nav-link-btn">Contact</button>
            <Link to="/login" className="login-link">Log in</Link>
          </div>
        </div>
        <div className={`mobile-menu ${menuOpen ? 'open' : ''}`}>
          <button onClick={() => scrollToSection('models')} className="mobile-link">Models</button>
          <button onClick={() => scrollToSection('about')} className="mobile-link">About</button>
          <button onClick={() => scrollToSection('faq')} className="mobile-link">FAQ</button>
          <button onClick={() => scrollToSection('contact')} className="mobile-link">Contact</button>
          <Link to="/login" className="mobile-login-link" onClick={() => setMenuOpen(false)}>Log in</Link>
        </div>
      </nav>

      {/* ===== HERO ===== */}
      <section className="hero-apple">
        <div className="hero-inner">
          <div className="hero-visual">
            <div className="hero-icon">⚖️</div>
          </div>
          <h1>High‑intent MVA leads<br /><span className="accent">delivered before your competitors</span></h1>
          <p className="hero-subtitle">
            Our proprietary verification system filters out low‑quality inquiries so you only pay for real, motivated motor vehicle accident prospects.
          </p>
          <div className="hero-buttons">
            <button onClick={() => scrollToSection('contact')} className="btn-primary">Get started</button>
            <span className="hero-note">No setup fee • Cancel anytime</span>
          </div>
          <div className="stats">
            <div><span>98%</span> <span>lead validity</span></div>
            <div><span>24/7</span> <span>real‑time delivery</span></div>
            <div><span>$0</span> <span>contract lock‑in</span></div>
          </div>
        </div>
      </section>

      {/* ===== MODELS ===== */}
      <section id="models" className="pricing-apple">
        <h2>Choose the model that fits your firm</h2>
        <div className="pricing-grid">
          <div className="card-apple">
            <h3>CPL</h3>
            <p>Pay only for each exclusive lead delivered. Simple, transparent, and no hidden fees.</p>
            <ul>
              <li>Real‑time email delivery</li>
              <li>Post‑incident screening</li>
              <li>No long‑term commitment</li>
            </ul>
            <button onClick={() => scrollToSection('contact')} className="card-link">Select CPL →</button>
          </div>
          <div className="card-apple featured">
            <div className="badge">Most popular</div>
            <h3>CPQ</h3>
            <p>We verify case value, liability, and contact accuracy before you pay.</p>
            <ul>
              <li>Pre‑verified by our team</li>
              <li>Highest intent leads</li>
              <li>Detailed qualification data</li>
            </ul>
            <button onClick={() => scrollToSection('contact')} className="card-link">Select CPQ →</button>
          </div>
          <div className="card-apple">
            <h3>CPA</h3>
            <p>You only pay when the lead signs a retainer. Zero risk, maximum alignment.</p>
            <ul>
              <li>Performance‑based model</li>
              <li>Full retainer verification</li>
              <li>Shared success</li>
            </ul>
            <button onClick={() => scrollToSection('contact')} className="card-link">Select CPA →</button>
          </div>
        </div>
      </section>

      {/* ===== ABOUT / FEATURES ===== */}
      <section id="about" className="features-apple">
        <h2>Why attorneys choose More MVA Cases</h2>
        <div className="features-grid">
          <div className="feature">
            <h4>Exclusive, real‑time leads</h4>
            <p>No recycled data. Each lead is delivered to you instantly and never shared with other firms.</p>
          </div>
          <div className="feature">
            <h4>Verified MVA incidents</h4>
            <p>We cross‑reference police reports, insurance data, and claimant intent to ensure authenticity.</p>
          </div>
          <div className="feature">
            <h4>Actionable dashboard</h4>
            <p>Track every lead, payment, and signed agreement from a single, intuitive interface.</p>
          </div>
        </div>

        <div className="features-grid" style={{ marginTop: '3rem' }}>
          <div className="feature">
            <h4>No contracts</h4>
            <p>Cancel anytime with a pro‑rated refund. We earn your business every month.</p>
          </div>
          <div className="feature">
            <h4>Live calls & web leads</h4>
            <p>Prospects can reach you by phone or web form – whichever they prefer.</p>
          </div>
          <div className="feature">
            <h4>Dedicated support</h4>
            <p>Our team helps you optimize targeting so you never waste a dollar.</p>
          </div>
        </div>
      </section>

      {/* ===== COMPARISON TABLE ===== */}
      <section className="compare-section">
        <div className="compare-inner">
          <div className="sec-head">
            <span className="kicker">Exclusive vs. shared</span>
            <h2 style={{ marginTop: 14 }}>Why the math favors exclusive</h2>
            <p>Shared leads look cheaper per record — until you price in the cases you lose to the four firms who got the same call.</p>
          </div>
          <div className="compare-table">
            <div className="crow head">
              <div>What you're paying for</div>
              <div className="cell">Shared leads</div>
              <div className="cell me"><span className="tag">More MVA Cases</span></div>
            </div>
            <div className="crow">
              <div className="feat">Sold only to your firm</div>
              <div className="cell"><span className="no">✕</span></div>
              <div className="cell me"><span className="yes">✓</span></div>
            </div>
            <div className="crow">
              <div className="feat">Delivered in real time</div>
              <div className="cell">Often aged</div>
              <div className="cell me"><span className="yes">✓</span></div>
            </div>
            <div className="crow">
              <div className="feat">Injury & fault screened</div>
              <div className="cell"><span className="no">✕</span></div>
              <div className="cell me"><span className="yes">✓</span></div>
            </div>
            <div className="crow">
              <div className="feat">TCPA consent on record</div>
              <div className="cell">Varies</div>
              <div className="cell me"><span className="yes">✓</span></div>
            </div>
            <div className="crow">
              <div className="feat">Bad leads replaced free</div>
              <div className="cell"><span className="no">✕</span></div>
              <div className="cell me"><span className="yes">✓</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section className="testimonials-apple">
        <h2>What attorneys say</h2>
        <div className="testimonials-grid">
          <div className="testimonial-card">
            <p>“More MVA Cases has transformed our intake. The leads are exclusive, verified, and arrive before our competitors even see them.”</p>
            <div className="testimonial-author">— Partner, California</div>
          </div>
          <div className="testimonial-card">
            <p>“We’ve tried other lead providers. This is the only one that actually delivers on quality and transparency.”</p>
            <div className="testimonial-author">— Managing Attorney, Texas</div>
          </div>
          <div className="testimonial-card">
            <p>“The dashboard makes it so easy to track every lead and payment. I know exactly what I’m getting.”</p>
            <div className="testimonial-author">— Solo Practitioner, Florida</div>
          </div>
        </div>
      </section>

      {/* ===== CRM INTEGRATIONS ===== */}
      <section className="crm-apple">
        <div className="crm-container">
          <h2>Send Exclusive, Real‑Time Legal Leads Directly to Your CRM</h2>
          <p className="crm-subtitle">
            Your leads can be delivered to <strong>any</strong> CRM or intake software.
            Below are just a few of the integrations we've established for our clients.
          </p>
          <div className="crm-logos-grid">
            <span className="crm-logo">LAW RULER</span>
            <span className="crm-logo">Clio</span>
            <span className="crm-logo">salesforce</span>
            <span className="crm-logo">ZOHO</span>
            <span className="crm-logo">LEADSpedia</span>
            <span className="crm-logo">boberdoo.com</span>
            <span className="crm-logo">webhooks</span>
            <span className="crm-logo">zapier</span>
            <span className="crm-logo more">And more...</span>
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section className="steps-section">
        <div className="steps-inner">
          <div className="sec-head">
            <span className="kicker">How it works</span>
            <h2 style={{ marginTop: 14 }}>Live leads in three steps</h2>
          </div>
          <div className="steps-grid">
            <div className="step-item">
              <div className="step-number">1</div>
              <h3>Set your criteria</h3>
              <p>Territory, case types and daily volume. We match supply to exactly what your firm signs.</p>
            </div>
            <div className="step-item">
              <div className="step-number">2</div>
              <h3>Receive live, exclusive leads</h3>
              <p>Qualified, consented claimants stream into your CRM and phone the moment they're ready.</p>
            </div>
            <div className="step-item">
              <div className="step-number">3</div>
              <h3>Sign & scale</h3>
              <p>Work fresh leads, flag any dud for free replacement, and dial volume up or down anytime.</p>
            </div>
          </div>
          <div className="steps-promise">
            <div className="chip">✓ No long‑term contract</div>
            <div className="chip">✓ Free bad‑lead replacement</div>
            <div className="chip">✓ Pause or cancel anytime</div>
          </div>
        </div>
      </section>

      {/* ===== PRACTICE AREAS ===== */}
      <section className="areas-apple">
        <h2>Areas we cover</h2>
        <div className="areas-grid">
          <span className="area-tag">Auto Accidents</span>
          <span className="area-tag">Personal Injury</span>
          <span className="area-tag">Truck Accidents</span>
          <span className="area-tag">Motorcycle Accidents</span>
          <span className="area-tag">Pedestrian Accidents</span>
          <span className="area-tag">Rideshare Accidents</span>
          <span className="area-tag">Hit & Run</span>
          <span className="area-tag">Uninsured Motorist</span>
          <span className="area-tag">Wrongful Death</span>
          <span className="area-tag">Catastrophic Injury</span>
        </div>
        <p className="areas-note">And more — contact us for categories beyond MVA.</p>
      </section>

      {/* ===== FAQ (EXPANDED) ===== */}
      <section id="faq" className="faq-apple">
        <h2>Frequently asked questions</h2>
        <div className="faq-list">
          {/* Existing questions */}
          <div className={`faq-item ${activeFaq === 0 ? 'open' : ''}`}>
            <button className="faq-question" onClick={() => toggleFaq(0)}>
              <span>How do exclusive leads work?</span>
              <span className="faq-icon">{activeFaq === 0 ? '−' : '+'}</span>
            </button>
            <div className="faq-answer">
              Each lead is delivered only to you. We never resell or share your leads with competing firms.
            </div>
          </div>
          <div className={`faq-item ${activeFaq === 1 ? 'open' : ''}`}>
            <button className="faq-question" onClick={() => toggleFaq(1)}>
              <span>What is real‑time delivery?</span>
              <span className="faq-icon">{activeFaq === 1 ? '−' : '+'}</span>
            </button>
            <div className="faq-answer">
              You receive leads instantly via email, SMS, or your CRM the moment they are verified.
            </div>
          </div>
          <div className={`faq-item ${activeFaq === 2 ? 'open' : ''}`}>
            <button className="faq-question" onClick={() => toggleFaq(2)}>
              <span>How do Live Calls work?</span>
              <span className="faq-icon">{activeFaq === 2 ? '−' : '+'}</span>
            </button>
            <div className="faq-answer">
              Prospects can call you directly through our tracking numbers. You only pay for calls that meet your criteria.
            </div>
          </div>
          <div className={`faq-item ${activeFaq === 3 ? 'open' : ''}`}>
            <button className="faq-question" onClick={() => toggleFaq(3)}>
              <span>How do you filter leads for quality?</span>
              <span className="faq-icon">{activeFaq === 3 ? '−' : '+'}</span>
            </button>
            <div className="faq-answer">
              We use a combination of AI screening and manual verification to confirm case validity and claimant intent.
            </div>
          </div>
          <div className={`faq-item ${activeFaq === 4 ? 'open' : ''}`}>
            <button className="faq-question" onClick={() => toggleFaq(4)}>
              <span>What are your targeting options?</span>
              <span className="faq-icon">{activeFaq === 4 ? '−' : '+'}</span>
            </button>
            <div className="faq-answer">
              You can target by geography, case type, injury severity, and more. We'll help you optimize.
            </div>
          </div>
          <div className={`faq-item ${activeFaq === 5 ? 'open' : ''}`}>
            <button className="faq-question" onClick={() => toggleFaq(5)}>
              <span>Why don’t you require contracts?</span>
              <span className="faq-icon">{activeFaq === 5 ? '−' : '+'}</span>
            </button>
            <div className="faq-answer">
              We believe in earning your business every month. No lock‑ins, no penalties.
            </div>
          </div>
          <div className={`faq-item ${activeFaq === 6 ? 'open' : ''}`}>
            <button className="faq-question" onClick={() => toggleFaq(6)}>
              <span>How much funding do I need?</span>
              <span className="faq-icon">{activeFaq === 6 ? '−' : '+'}</span>
            </button>
            <div className="faq-answer">
              You decide. Start with as little as $500 and scale up as you see results.
            </div>
          </div>

          {/* New questions (from Perk) */}
          <div className={`faq-item ${activeFaq === 7 ? 'open' : ''}`}>
            <button className="faq-question" onClick={() => toggleFaq(7)}>
              <span>What types of MVA leads do you offer?</span>
              <span className="faq-icon">{activeFaq === 7 ? '−' : '+'}</span>
            </button>
            <div className="faq-answer">
              We offer both digital motor vehicle accident leads and live call transfers. Digital leads are posted directly into your system, while live transfers are qualified by our intake team before being connected to your firm.
            </div>
          </div>
          <div className={`faq-item ${activeFaq === 8 ? 'open' : ''}`}>
            <button className="faq-question" onClick={() => toggleFaq(8)}>
              <span>Are these exclusive leads?</span>
              <span className="faq-icon">{activeFaq === 8 ? '−' : '+'}</span>
            </button>
            <div className="faq-answer">
              Yes, all leads are exclusive to you. We never sell the same lead to another firm.
            </div>
          </div>
          <div className={`faq-item ${activeFaq === 9 ? 'open' : ''}`}>
            <button className="faq-question" onClick={() => toggleFaq(9)}>
              <span>How old are the accident leads?</span>
              <span className="faq-icon">{activeFaq === 9 ? '−' : '+'}</span>
            </button>
            <div className="faq-answer">
              Our standard filter is that the accident occurred within the last year. More specific time windows can be set.
            </div>
          </div>
          <div className={`faq-item ${activeFaq === 10 ? 'open' : ''}`}>
            <button className="faq-question" onClick={() => toggleFaq(10)}>
              <span>How do live transfers work?</span>
              <span className="faq-icon">{activeFaq === 10 ? '−' : '+'}</span>
            </button>
            <div className="faq-answer">
              Our call center qualifies the potential client and transfers the call live to your intake team — delivering a warm, vetted opportunity.
            </div>
          </div>
          <div className={`faq-item ${activeFaq === 11 ? 'open' : ''}`}>
            <button className="faq-question" onClick={() => toggleFaq(11)}>
              <span>What happens if a lead is invalid?</span>
              <span className="faq-icon">{activeFaq === 11 ? '−' : '+'}</span>
            </button>
            <div className="faq-answer">
              Invalid leads can be replaced when they fail agreed criteria, such as disconnected numbers, wrong geography, no injury, or having an attorney already.
            </div>
          </div>
          <div className={`faq-item ${activeFaq === 12 ? 'open' : ''}`}>
            <button className="faq-question" onClick={() => toggleFaq(12)}>
              <span>What is your replacement policy?</span>
              <span className="faq-icon">{activeFaq === 12 ? '−' : '+'}</span>
            </button>
            <div className="faq-answer">
              For digital leads, invalid leads may be replaced. For live transfers, if a prospect misrepresents key facts and you can prove it, we add a replacement transfer.
            </div>
          </div>
          <div className={`faq-item ${activeFaq === 13 ? 'open' : ''}`}>
            <button className="faq-question" onClick={() => toggleFaq(13)}>
              <span>What if our cost per signed case is too high?</span>
              <span className="faq-icon">{activeFaq === 13 ? '−' : '+'}</span>
            </button>
            <div className="faq-answer">
              We can include a target cost per acquisition. If performance exceeds that, additional leads are provided until the target is met.
            </div>
          </div>
          <div className={`faq-item ${activeFaq === 14 ? 'open' : ''}`}>
            <button className="faq-question" onClick={() => toggleFaq(14)}>
              <span>How do we start?</span>
              <span className="faq-icon">{activeFaq === 14 ? '−' : '+'}</span>
            </button>
            <div className="faq-answer">
              Most firms start with a prepaid test campaign. We structure it by state, market, or lead type, with clear pricing and criteria.
            </div>
          </div>
        </div>
        <div className="faq-cta">
          <button onClick={() => scrollToSection('contact')} className="faq-cta-link">Still have questions? Contact us →</button>
        </div>
      </section>

      {/* ===== CONTACT ===== */}
      <section id="contact" className="contact-apple">
        <div className="contact-container">
          <h2>Get in touch</h2>
          <div className="contact-card">
            <div className="contact-details">
              <div className="contact-item">
                <span className="contact-label">Phone:</span>
                <a href="tel:+12092775177" className="contact-value">209-277-5177</a>
              </div>
              <div className="contact-item">
                <span className="contact-label">Email:</span>
                <a href="mailto:mark@moremvacases.com" className="contact-value">mark@moremvacases.com</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="cta-apple">
        <div className="cta-inner">
          <h2>Ready to scale your MVA practice?</h2>
          <p>Join hundreds of attorneys who have transformed their intake with our exclusive leads.</p>
          <button onClick={() => scrollToSection('contact')} className="btn-cta">Claim your first leads →</button>
        </div>
      </section>

      {/* ===== FOOTER (with ref for observer) ===== */}
      <footer className="footer-apple" ref={footerRef}>
        <div className="footer-container">
          <div className="footer-logo">More MVA Cases</div>
          <div className="footer-links">
            <button onClick={() => scrollToSection('models')} className="footer-link-btn">Models</button>
            <button onClick={() => scrollToSection('about')} className="footer-link-btn">About</button>
            <button onClick={() => scrollToSection('faq')} className="footer-link-btn">FAQ</button>
            <button onClick={() => scrollToSection('contact')} className="footer-link-btn">Contact</button>
            <Link to="/login" className="footer-login-link">Log in</Link>
          </div>
          <div className="footer-copyright">© 2026 More MVA Cases. All rights reserved.</div>
        </div>
      </footer>

      {/* ===== STICKY BAR ===== */}
      <div className="sticky-bar">
        <div className="sticky-inner">
          <span className="sticky-text"><strong>Ready to grow?</strong> Get exclusive MVAs →</span>
          <button onClick={() => scrollToSection('contact')} className="btn-primary">Contact us</button>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;